import React, { useEffect, useRef, useState, useMemo } from 'react';
import { getVehicles, getPositions, getTrack, getVideoFilesDay } from '../utils/api';
import CmsV6VodPlayer from '../components/CmsV6VodPlayer';

// ── Helpers ───────────────────────────────────────────────────
const normalize   = (v) => Math.abs(v) > 1000 ? v / 1000000 : v;
const pad         = (n) => String(n).padStart(2, '0');
const fmtSec      = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor(s % 3600 / 60))}:${pad(s % 60)}`;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_ABBR    = ['S','M','T','W','T','F','S'];

// ── Leaflet loader ────────────────────────────────────────────
function ensureLeaflet() {
  return new Promise(resolve => {
    if (window.L) { resolve(); return; }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.css';
      document.head.appendChild(link);
    }
    const existing = document.getElementById('leaflet-js');
    if (existing) { existing.addEventListener('load', resolve); return; }
    const s = document.createElement('script');
    s.id = 'leaflet-js';
    s.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.js';
    s.onload = resolve;
    document.head.appendChild(s);
  });
}

// ── Mini calendar ─────────────────────────────────────────────
function MiniCalendar({ year, month, day, onDayClick, onPrevMonth, onNextMonth, daysWithData, calLoading }) {
  const td       = new Date();
  const todayY   = td.getFullYear();
  const todayM   = td.getMonth() + 1;
  const todayD   = td.getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const lastDay  = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:10,
      padding:'10px 8px', userSelect:'none' }}>

      {/* Month header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <button onClick={onPrevMonth}
          style={{ width:22, height:22, border:'0.5px solid #ddd', background:'white',
            borderRadius:4, fontSize:10, cursor:'pointer' }}>◀</button>
        <span style={{ fontSize:12, fontWeight:600, color:'#1f2937' }}>
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={onNextMonth}
          style={{ width:22, height:22, border:'0.5px solid #ddd', background:'white',
            borderRadius:4, fontSize:10, cursor:'pointer' }}>▶</button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', marginBottom:2 }}>
        {DAY_ABBR.map((d, i) => (
          <div key={i} style={{ textAlign:'center', fontSize:9, color:'#9ca3af', fontWeight:600, padding:'2px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'2px 0' }}>
        {cells.map((d, i) => {
          const sel    = d !== null && d === day;
          const tod    = d !== null && year === todayY && month === todayM && d === todayD;
          const hasDat = d !== null && daysWithData.has(d);
          return (
            <div key={i} onClick={() => d && onDayClick(d)}
              style={{
                position:'relative', textAlign:'center', fontSize:11, padding:'4px 2px',
                borderRadius:4, cursor: d ? 'pointer' : 'default', boxSizing:'border-box',
                background: sel ? '#185FA5' : hasDat ? '#dcfce7' : 'transparent',
                border:     hasDat && !sel ? '1px solid #86efac' : '1px solid transparent',
                color:      sel ? 'white' : d ? '#374151' : 'transparent',
                fontWeight: sel || tod ? 600 : 400,
              }}>
              {d || ''}
              {tod && !sel && (
                <div style={{ position:'absolute', bottom:1, left:'50%', transform:'translateX(-50%)',
                  width:3, height:3, borderRadius:'50%', background:'#185FA5' }} />
              )}
            </div>
          );
        })}
      </div>

      {calLoading && (
        <div style={{ textAlign:'center', fontSize:9, color:'#9ca3af', marginTop:6 }}>
          Checking availability...
        </div>
      )}
    </div>
  );
}

// ── Route map with live position marker ───────────────────────
function RouteMap({ points, currentSec, isPlaying }) {
  const mapRef    = useRef(null);
  const mapInst   = useRef(null);
  const layers    = useRef([]);
  const posMarker = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ensureLeaflet().then(() => {
      if (cancelled || !mapRef.current || mapInst.current) return;
      const L   = window.L;
      const map = L.map(mapRef.current, {
        center: [3.139, 101.687], zoom: 10,
        zoomControl: true, attributionControl: false,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);
      // Inject pulse keyframes once — used by the position marker icon
      if (!document.getElementById('map-pulse-css')) {
        const s = document.createElement('style');
        s.id = 'map-pulse-css';
        s.textContent = '@keyframes pulse{0%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:0.7}100%{transform:scale(1);opacity:1}}';
        document.head.appendChild(s);
      }
      mapInst.current = map;
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; }
      layers.current = []; posMarker.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapInst.current) return;
    const L   = window.L;
    const map = mapInst.current;
    layers.current.forEach(l => map.removeLayer(l));
    layers.current = [];
    if (posMarker.current) { map.removeLayer(posMarker.current); posMarker.current = null; }

    const latlngs = (points || [])
      .filter(p => p.lat && p.lng)
      .map(p => [normalize(p.lat), normalize(p.lng)]);
    if (latlngs.length < 2) return;

    const line = L.polyline(latlngs, { color: '#4b7ca8', weight: 2, opacity: 0.6 }).addTo(map);
    const dot  = color => L.divIcon({
      className: '',
      html: `<div style="width:8px;height:8px;background:${color};border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize: [8, 8], iconAnchor: [4, 4],
    });
    layers.current.push(
      line,
      L.marker(latlngs[0],                  { icon: dot('#22c55e') }).addTo(map),
      L.marker(latlngs[latlngs.length - 1], { icon: dot('#ef4444') }).addTo(map),
    );
    map.fitBounds(line.getBounds(), { padding: [20, 20] });
  }, [ready, points]);

  useEffect(() => {
    if (!ready || !mapInst.current) return;
    const L = window.L;

    // Hide marker when not playing
    if (!isPlaying) {
      if (posMarker.current) { mapInst.current.removeLayer(posMarker.current); posMarker.current = null; }
      return;
    }

    if (!points?.length) return;
    const toSec = (t) => { const m = String(t || '').match(/(\d{1,2}):(\d{2}):(\d{2})$/); return m ? +m[1]*3600 + +m[2]*60 + +m[3] : null; };
    const valid = (points || [])
      .filter(p => p.lat && p.lng)
      .map(p => ({ lat: normalize(p.lat), lng: normalize(p.lng), sec: toSec(p.gpsTime) }))
      .filter(p => p.sec !== null);
    if (!valid.length) return;

    const closest = valid.reduce((best, p) =>
      Math.abs(p.sec - currentSec) < Math.abs(best.sec - currentSec) ? p : best
    );
    const latlng = [closest.lat, closest.lng];

    if (posMarker.current) {
      posMarker.current.setLatLng(latlng);
    } else {
      const icon = L.divIcon({
        className: '',
        html: '<div style="width:18px;height:18px;background:#f59e0b;border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px rgba(245,158,11,0.4),0 2px 8px rgba(0,0,0,.6);animation:pulse 1.5s ease-in-out infinite"></div>',
        iconSize: [18, 18], iconAnchor: [9, 9],
      });
      posMarker.current = L.marker(latlng, { icon }).addTo(mapInst.current);
    }
    mapInst.current.panTo(latlng, { animate: true, duration: 0.3 });
  }, [ready, points, currentSec, isPlaying]);

  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      <div ref={mapRef} style={{ width:'100%', height:'100%' }} />
      {!points?.length && (
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
          justifyContent:'center', pointerEvents:'none' }}>
          <span style={{ color:'#555', fontSize:12 }}>No route data</span>
        </div>
      )}
    </div>
  );
}

// ── Video timeline ────────────────────────────────────────────
// channels is derived from files — only channels present in files get a row
function VideoTimeline({ files, currentSec, onSeek }) {
  const innerRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const channels = useMemo(
    () => [...new Set(files.map(f => f.chn))].sort((a, b) => a - b),
    [files]
  );

  const secFromX = (clientX) => {
    const rect = innerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return currentSec;
    return Math.round(Math.max(0, Math.min(86399, (clientX - rect.left) / rect.width * 86400)));
  };

  const onDown  = (e) => { e.preventDefault(); setDragging(true); onSeek(secFromX(e.clientX)); };
  const onMove  = (e) => { if (dragging) onSeek(secFromX(e.clientX)); };
  const onUp    = ()  => setDragging(false);
  const onLeave = ()  => setDragging(false);

  const px  = (sec) => `${(sec / 86400 * 100).toFixed(3)}%`;
  const wpx = (b, e) => `${Math.max(0.1, (e - b) / 86400 * 100).toFixed(3)}%`;

  const ROW_H  = 14;
  const AXIS_H = 18;

  return (
    <div style={{ background:'#111', borderRadius:8, flexShrink:0 }}>
      <div style={{ display:'flex' }}>

        {/* Fixed label column */}
        <div style={{ flexShrink:0, width:38, paddingTop:AXIS_H,
          borderRight:'1px solid #1e1e1e', background:'#111' }}>
          <div style={{ height:ROW_H, display:'flex', alignItems:'center',
            justifyContent:'flex-end', paddingRight:5, fontSize:9, color:'#555' }}>ALL</div>
          {channels.map(ch => (
            <div key={ch} style={{ height:ROW_H, display:'flex', alignItems:'center',
              justifyContent:'flex-end', paddingRight:5, fontSize:9, color:'#555' }}>
              CH{ch + 1}
            </div>
          ))}
        </div>

        {/* Timeline area — fills remaining width, no horizontal scroll */}
        <div style={{ flex:1, overflow:'hidden' }}>
          <div
            ref={innerRef}
            style={{ position:'relative', width:'100%', userSelect:'none', cursor:'crosshair' }}
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onLeave}
          >
            {/* Hour grid lines — percentage positions */}
            {Array.from({ length: 25 }, (_, h) => (
              <div key={h} style={{ position:'absolute', left:`${(h / 24 * 100).toFixed(3)}%`, top:0, bottom:0, width:1,
                background: h % 6 === 0 ? '#252525' : '#191919', pointerEvents:'none' }} />
            ))}

            {/* Time axis */}
            <div style={{ position:'relative', height:AXIS_H, borderBottom:'1px solid #1e1e1e' }}>
              {Array.from({ length: 13 }, (_, i) => {
                const h = i * 2;
                return (
                  <span key={h} style={{ position:'absolute', left:`${(h * 3600 / 86400 * 100).toFixed(3)}%`, fontSize:9,
                    color:'#555', top:5, transform:'translateX(-50%)', pointerEvents:'none' }}>
                    {pad(h)}:00
                  </span>
                );
              })}
            </div>

            {/* ALL row */}
            <div style={{ position:'relative', height:ROW_H, background:'#161616', borderBottom:'1px solid #1c1c1c' }}>
              {files.map((f, i) => (
                <div key={i} style={{ position:'absolute', left:px(f.beg), width:wpx(f.beg, f.end),
                  top:3, height:ROW_H - 6, background:'#185FA5', borderRadius:2, opacity:0.9, pointerEvents:'none' }} />
              ))}
            </div>

            {/* Per-channel rows (only channels present in files) */}
            {channels.map(ch => (
              <div key={ch} style={{ position:'relative', height:ROW_H, background:'#0f0f0f', borderBottom:'1px solid #191919' }}>
                {files.filter(f => f.chn === ch).map((f, i) => (
                  <div key={i} style={{ position:'absolute', left:px(f.beg), width:wpx(f.beg, f.end),
                    top:3, height:ROW_H - 6, background:'#1e7fc2', borderRadius:2, opacity:0.9, pointerEvents:'none' }} />
                ))}
              </div>
            ))}

            {/* Playhead */}
            <div style={{ position:'absolute', left:px(currentSec), top:0, bottom:0,
              width:1, background:'#ef4444', pointerEvents:'none', zIndex:10 }}>
              <div style={{ position:'absolute', top:0, left:-4,
                width:0, height:0, borderLeft:'4px solid transparent',
                borderRight:'4px solid transparent', borderTop:'7px solid #ef4444' }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign:'center', fontSize:10, color:'#555',
        padding:'3px 0 5px', borderTop:'1px solid #1a1a1a' }}>
        {fmtSec(currentSec)}
      </div>
    </div>
  );
}

// ── Toolbar button ────────────────────────────────────────────
function CtrlBtn({ onClick, disabled, children, title }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{ width:28, height:28, border:'0.5px solid #e5e7eb', background:'white',
        borderRadius:4, fontSize:13, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        display:'flex', alignItems:'center', justifyContent:'center' }}>
      {children}
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────
export default function PlaybackPage() {
  const today = new Date();

  const [vehicles,      setVehicles]      = useState([]);
  const [vehiIdno,      setVehiIdno]      = useState('');
  const [vehicleOnline, setVehicleOnline] = useState(true);
  const [year,          setYear]          = useState(today.getFullYear());
  const [month,         setMonth]         = useState(today.getMonth() + 1);
  const [day,           setDay]           = useState(today.getDate());
  const [files,         setFiles]         = useState([]);
  const [trackPoints,   setTrackPoints]   = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [currentSec,    setCurrentSec]    = useState(0);
  const [vodUrls,       setVodUrls]       = useState(null);
  const [isPlaying,     setIsPlaying]     = useState(false);
  const [speed,         setSpeed]         = useState('1x');
  const [daysWithData,  setDaysWithData]  = useState(new Set());
  const [calLoading,    setCalLoading]    = useState(false);
  const [streamType,    setStreamType]    = useState('sub');

  const availCache    = useRef(null);  // { key: string, data: Set }
  const availFetchKey = useRef(null);  // tracks the latest in-flight request

  // ── Month availability (green dots in calendar) ────────────
  const loadMonthAvailability = async (vid, y, m) => {
    if (!vid) return;
    const key = `${vid}_${y}_${m}`;
    if (availCache.current?.key === key) {
      setDaysWithData(availCache.current.data);
      return;
    }
    availFetchKey.current = key;
    setCalLoading(true);
    setDaysWithData(new Set());
    const daysInMonth = new Date(y, m, 0).getDate();
    try {
      const results = await Promise.all(
        Array.from({ length: daysInMonth }, (_, i) =>
          getVideoFilesDay(vid, y, m, i + 1).catch(() => ({ files: [] }))
        )
      );
      if (availFetchKey.current !== key) return; // superseded by a newer request
      const set = new Set();
      results.forEach((r, i) => { if ((r.files || []).length > 0) set.add(i + 1); });
      availCache.current = { key, data: set };
      setDaysWithData(set);
    } catch {}
    if (availFetchKey.current === key) setCalLoading(false);
  };

  // ── Load vehicles on mount ─────────────────────────────────
  useEffect(() => {
    Promise.all([getVehicles(), getPositions()]).then(([vd, pd]) => {
      const posMap = {};
      (pd.positions || []).forEach(p => { posMap[String(p.vehiIdno)] = p; });
      const merged = (vd.vehicles || [])
        .map(v => ({ ...v, online: posMap[String(v.devId)]?.online ?? false }))
        .sort((a, b) => (b.online ? 1 : 0) - (a.online ? 1 : 0));
      setVehicles(merged);
      if (merged.length) {
        const firstId = merged[0].devId || '';
        setVehiIdno(firstId);
        setVehicleOnline(merged[0].online);
        loadMonthAvailability(firstId, today.getFullYear(), today.getMonth() + 1);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVehicleChange = (e) => {
    const id = e.target.value;
    const v  = vehicles.find(v => String(v.devId) === String(id));
    setVehiIdno(id);
    setVehicleOnline(v?.online ?? false);
    setFiles([]); setTrackPoints([]); setVodUrls(null); setIsPlaying(false); setCurrentSec(0);
    loadMonthAvailability(id, year, month);
  };

  const prevMonth = () => {
    const newM = month === 1 ? 12 : month - 1;
    const newY = month === 1 ? year - 1 : year;
    setMonth(newM);
    if (month === 1) setYear(y => y - 1);
    loadMonthAvailability(vehiIdno, newY, newM);
  };

  const nextMonth = () => {
    const newM = month === 12 ? 1 : month + 1;
    const newY = month === 12 ? year + 1 : year;
    setMonth(newM);
    if (month === 12) setYear(y => y + 1);
    loadMonthAvailability(vehiIdno, newY, newM);
  };

  // ── Search ─────────────────────────────────────────────────
  // Accepts explicit date overrides so calendar day clicks bypass stale state.
  const handleSearch = async (oy, om, od) => {
    const y = oy ?? year;
    const m = om ?? month;
    const d = od ?? day;
    if (!vehiIdno) return;
    setLoading(true); setFiles([]); setTrackPoints([]); setVodUrls(null); setIsPlaying(false);
    const dateStr  = `${y}-${pad(m)}-${pad(d)}`;
    const streamVal = streamType === 'main' ? 0 : 1;
    try {
      const [fd, td] = await Promise.all([
        getVideoFilesDay(vehiIdno, y, m, d, streamVal),
        getTrack(vehiIdno, `${dateStr} 00:00:00`, `${dateStr} 23:59:59`),
      ]);
      console.log('[Search] files:', fd.files?.length, 'tracks:', td.tracks?.length);
      const f = fd.files || [];
      setFiles(f);
      setTrackPoints(td.tracks || []);
      if (f.length) setCurrentSec(f[0].beg);
    } catch (err) {
      console.error('[Search]', err);
    }
    setLoading(false);
  };

  const handleDayClick = (d) => {
    setDay(d);
    handleSearch(year, month, d);
  };

  // ── Seek — builds 4-slot URL array (one per channel 0-3) ──
  const handleSeek = (sec) => {
    const s         = Math.max(0, Math.min(86399, Math.round(sec)));
    const streamVal = streamType === 'main' ? '0' : '1';
    setCurrentSec(s);
    // One URL slot per channel; channels without a recording at s get null
    const byChannel = {};
    files
      .filter(f => f.beg <= s && f.end > s)
      .forEach(f => { byChannel[f.chn] = f.PlaybackUrlWs.replace(/STREAM=\d/, `STREAM=${streamVal}`); });
    const urls      = [0, 1, 2, 3].map(ch => byChannel[ch] || null);
    const hasActive = urls.some(u => u !== null);
    if (hasActive) {
      setVodUrls(urls);
      setIsPlaying(true);
    } else {
      setVodUrls(null);
      setIsPlaying(false);
    }
  };

  // ── Playback controls ──────────────────────────────────────
  const sortedBegs = useMemo(
    () => [...new Set(files.map(f => f.beg))].sort((a, b) => a - b),
    [files]
  );

  const handlePlayPause = () => {
    if (isPlaying) { setVodUrls(null); setIsPlaying(false); }
    else handleSeek(currentSec);
  };
  const handleStop  = () => { setVodUrls(null); setIsPlaying(false); if (sortedBegs.length) setCurrentSec(sortedBegs[0]); };
  const handleFirst = () => { if (sortedBegs.length) handleSeek(sortedBegs[0]); };
  const handleLast  = () => { if (sortedBegs.length) handleSeek(sortedBegs[sortedBegs.length - 1]); };
  const handlePrev  = () => { const b = [...sortedBegs].reverse().find(b => b < currentSec); if (b !== undefined) handleSeek(b); };
  const handleNext  = () => { const b = sortedBegs.find(b => b > currentSec); if (b !== undefined) handleSeek(b); };

  const hasFiles = files.length > 0;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10, height:'100%', overflow:'hidden' }}>

      {/* Toolbar */}
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:10,
        padding:'10px 14px', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>

        {/* Vehicle selector */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
            background: vehicleOnline ? '#22c55e' : '#9ca3af' }} />
          <select value={vehiIdno} onChange={handleVehicleChange}
            style={{ fontSize:12, padding:'5px 8px', border:'0.5px solid #ddd', borderRadius:6, minWidth:140 }}>
            {vehicles.map((v, i) => (
              <option key={i} value={v.devId || v.id}>
                {v.online ? '● ' : '○ '}{v.displayName || `Vehicle ${i + 1}`}
              </option>
            ))}
          </select>
        </div>

        {/* Stream type selector */}
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:11, color:'#374151' }}>
          <label style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer' }}>
            <input type="radio" name="streamType" checked={streamType === 'main'}
              onChange={() => setStreamType('main')} style={{ cursor:'pointer' }} />
            Main
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer' }}>
            <input type="radio" name="streamType" checked={streamType === 'sub'}
              onChange={() => setStreamType('sub')} style={{ cursor:'pointer' }} />
            Sub
          </label>
        </div>

        <button onClick={() => handleSearch()} disabled={!vehiIdno || loading}
          style={{ padding:'6px 14px', background:'#185FA5', color:'white', border:'none',
            borderRadius:6, fontSize:12, fontWeight:500,
            opacity: vehiIdno && !loading ? 1 : 0.5,
            cursor: vehiIdno && !loading ? 'pointer' : 'not-allowed' }}>
          {loading ? 'Loading...' : '🔍 Search'}
        </button>

        <div style={{ flex:1, minWidth:4 }} />

        {/* Playback controls */}
        <div style={{ display:'flex', alignItems:'center', gap:3 }}>
          <CtrlBtn onClick={handleFirst}     disabled={!hasFiles} title="First segment">⏮</CtrlBtn>
          <CtrlBtn onClick={handlePrev}      disabled={!hasFiles} title="Previous segment">⏪</CtrlBtn>
          <CtrlBtn onClick={handlePlayPause} disabled={!hasFiles} title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '⏸' : '▶'}
          </CtrlBtn>
          <CtrlBtn onClick={handleStop}      disabled={!isPlaying} title="Stop">⏹</CtrlBtn>
          <CtrlBtn onClick={handleNext}      disabled={!hasFiles} title="Next segment">⏩</CtrlBtn>
          <CtrlBtn onClick={handleLast}      disabled={!hasFiles} title="Last segment">⏭</CtrlBtn>
        </div>

        <select value={speed} onChange={e => setSpeed(e.target.value)}
          style={{ fontSize:11, padding:'5px 6px', border:'0.5px solid #ddd', borderRadius:6, cursor:'pointer' }}>
          {['0.5x','1x','2x','4x'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {hasFiles && (
          <span style={{ fontSize:12, fontWeight:500, color:'#374151',
            fontVariantNumeric:'tabular-nums', minWidth:58 }}>
            {fmtSec(currentSec)}
          </span>
        )}
      </div>

      {/* Main area: calendar | video | map */}
      <div style={{ display:'flex', gap:10, flex:1, minHeight:0 }}>

        {/* Calendar panel */}
        <div style={{ flexShrink:0, width:174 }}>
          <MiniCalendar
            year={year} month={month} day={day}
            onDayClick={handleDayClick}
            onPrevMonth={prevMonth}
            onNextMonth={nextMonth}
            daysWithData={daysWithData}
            calLoading={calLoading}
          />
        </div>

        {/* Video player */}
        <div style={{ flex:1, minWidth:0, background:'#0a0a0a', border:'0.5px solid #333',
          borderRadius:12, overflow:'hidden', position:'relative' }}>
          {!vehicleOnline ? (
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center', gap:8 }}>
              <div style={{ fontSize:32 }}>📵</div>
              <div style={{ fontWeight:500, color:'#aaa', fontSize:13 }}>Vehicle offline</div>
              <div style={{ fontSize:11, color:'#555' }}>Playback unavailable — device must be online</div>
            </div>
          ) : vodUrls ? (
            <CmsV6VodPlayer vodUrls={vodUrls} />
          ) : (
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center', gap:10 }}>
              {loading ? (
                <div style={{ color:'#555', fontSize:12 }}>Loading recordings...</div>
              ) : hasFiles ? (
                <div style={{ color:'#555', fontSize:13 }}>Click the timeline to start playback</div>
              ) : (
                <>
                  <svg width="40" height="40" fill="#333" viewBox="0 0 24 24">
                    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z"/>
                  </svg>
                  <div style={{ color:'#555', fontSize:13 }}>Pick a day from the calendar to search recordings</div>
                </>
              )}
            </div>
          )}
        </div>

        {/* GPS route map */}
        <div style={{ flexShrink:0, width:'28%', border:'0.5px solid #e8e8e8',
          borderRadius:12, overflow:'hidden' }}>
          <RouteMap points={trackPoints} currentSec={currentSec} isPlaying={isPlaying} />
        </div>
      </div>

      {/* Timeline — capped height so SDK toolbar doesn't push it off screen */}
      <div style={{ flexShrink:0, maxHeight:110, overflow:'hidden' }}>
        <VideoTimeline files={files} currentSec={currentSec} onSeek={handleSeek} />
      </div>
    </div>
  );
}
